import { useRef } from 'react'

import {
  getAllPagesData,
  getExpandsParamForColumnsObjectCommand,
} from '@components/TimeLineGrid/helpers'

import { useExecuteCommandMutation } from '@redux/api'

import { ListQueryResponse } from '@hooks'
import { getCommandRequestData, getPathString } from '@helpers'
import { TIME_LINE_PAGINATION_SIZE } from '@constants'
import { ExecuteCommandPayload, ObjectDataRecord, TimeLineDTOType } from '@types'

type GetDataFromLinkObjectCommand = {
  config: TimeLineDTOType
  rsqlFilter: string | null
  objectXKeysForLoad?: unknown[]
  skipCache?: boolean
}

type CachedColumnsData = {
  rsqlFilter: string | null
  data: ObjectDataRecord[]
}

export const useGetDataFromColumnsObject = () => {
  const [executeCommand] = useExecuteCommandMutation()
  const cacheRef = useRef<CachedColumnsData | null>(null)

  const getData = async (props: GetDataFromLinkObjectCommand) => {
    const { config, rsqlFilter, objectXKeysForLoad, skipCache = false } = props

    if (cacheRef.current && cacheRef.current.rsqlFilter === rsqlFilter && !skipCache) {
      return cacheRef.current.data
    }

    const command = config.objectX.columnsObject?.command
    const params = command?.parameters ? getCommandRequestData(command.parameters, {}) : {}

    const filter = []

    if (rsqlFilter) {
      filter.push(rsqlFilter)
    }

    if (params.filter) {
      filter.push(params.filter)
    }

    if (objectXKeysForLoad?.length) {
      // Экранируем кавычки в значениях (если это строки)
      const escapedKeyFieldValues = objectXKeysForLoad
        .map(keyFieldValue => `"${keyFieldValue}"`)
        .join(',')

      const objectYKeyFieldValuesFilter = `${getPathString(
        config.objectX.fieldFromColumnsObjectToLink
      )}=in=(${escapedKeyFieldValues})`

      filter.push(objectYKeyFieldValuesFilter)
    }

    const commandParams: ExecuteCommandPayload = {
      objectCode: config.objectX.columnsObject?.objectCode as string,
      commandCode: command?.name as string,
      transformResponseWithPagination: true,
      params: {
        ...params,
        filter: filter.join(';'),
        expands: getExpandsParamForColumnsObjectCommand({ config }),
      },
      size: TIME_LINE_PAGINATION_SIZE,
      method: command?.httpMethod,
      body:
        command?.body && command?.httpMethod !== 'GET'
          ? getCommandRequestData(command.body, {})
          : undefined,
    }

    const columns = await executeCommand(commandParams)
      .unwrap()
      .then(result =>
        getAllPagesData({
          firstPageData: result as ListQueryResponse<ObjectDataRecord>,
          executeCommand,
          commandParams,
        })
      )

    cacheRef.current = { rsqlFilter, data: columns }

    return columns
  }

  return {
    getDataFromColumnsObject: getData,
  }
}
